import { BarChart3 } from 'lucide-react';
import { LayoutDashboard } from 'lucide-react';
import { LineChart } from 'lucide-react';
import { Settings } from 'lucide-react';
import { Zap } from 'lucide-react';
import { Header as HeaderPrimitive } from "./ui/header";
import ThemeToggle from "@/components/ThemeToggle";
import NutMessageToggle from "@/components/NutMessageToggle";
import NutMessageOverlay from "@/components/NutMessageOverlay";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/hooks/redux";
import { RootState } from "../store";
import SharedCopy from "@/config/SharedCopy.json";

// Navigation items are either links or sidebar content.
const navItems: Array<{
  path: string;
  label: string;
  icon: React.ElementType | undefined;
  ariaLabel?: string;
} | { sidebarContent: React.ElementType }> = [
  { path: '/', label: SharedCopy["nav.dashboard"].text, icon: LayoutDashboard },
    { path: '/graphs', label: SharedCopy["nav.graphs"].text, icon: LineChart },
    { path: '/events', label: SharedCopy["nav.events"].text, icon: Zap },
    { path: '/settings', label: SharedCopy["nav.settings"].text, icon: Settings }
  // { path: '/', label: 'Home', icon: Home, ariaLabel: "Home" },
];

export function Header({ className }: { className?: string }) {
  const location = useLocation();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const appTitle = useAppSelector((state: RootState) => state.ui.customVariables['--app-title'] || SharedCopy["app.defaultTitle"].text);

  useEffect(() => {
    document.title = appTitle;
  }, [appTitle]);

  // When there is content to render in the sidebar, we force it to always render.
  const hasSidebarContent = navItems.some((item) => "sidebarContent" in item);

  return (
    <>
      <HeaderPrimitive className={cn("sticky top-0 z-50 glass-effect border-b border-border/50", className)} hasSidebarContent={hasSidebarContent}>
        <HeaderPrimitive.Left>
          <HeaderPrimitive.OverflowToggle />
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 shadow-sm">
              <BarChart3 className="h-4.5 w-4.5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground tracking-tight">{appTitle}</span>
          </div>
        </HeaderPrimitive.Left>
        <HeaderPrimitive.Center>
          {navItems.map((item, index) => {
            if ("sidebarContent" in item) {
              return (
                <HeaderPrimitive.SidebarContent key={index}>
                  <item.sidebarContent />
                </HeaderPrimitive.SidebarContent>
              );
            }
            const Icon = item.icon;
            // Mark as active if the current path matches
            const isActive = location.pathname === item.path;
            return (
              <HeaderPrimitive.Link
                key={item.path}
                to={item.path}
                aria-label={item.ariaLabel || item.label}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[\(\)]/g, '')}`}
                aria-current={isActive ? "page" : undefined}
              >
                {Icon && <Icon aria-hidden className="size-3.5" />}
                <span>{item.label}</span>
              </HeaderPrimitive.Link>
            );
          })}
        </HeaderPrimitive.Center>
        <HeaderPrimitive.Right>
          <NutMessageToggle onClick={() => setIsOverlayOpen(true)} />
          <ThemeToggle />
        </HeaderPrimitive.Right>
      </HeaderPrimitive>

      <NutMessageOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
      />
    </>
  );
}

export default Header;